/** Backend exit / resignation workflow state */
export const ExitStatus = {
  Draft: "DRAFT",
  Submitted: "SUBMITTED",
  PendingApproval: "PENDING_APPROVAL",
  Approved: "APPROVED",
  Rejected: "REJECTED",
  Withdrawn: "WITHDRAWN",
  Completed: "COMPLETED",
} as const;

export type ExitStatus = (typeof ExitStatus)[keyof typeof ExitStatus];

export interface ResignationReasonOption {
  id: string;
  label?: string;
  /** Master-style options from API (same pattern as IMasterConfigOption) */
  displayName?: string;
  name?: string;
  reasonText?: string;
  title?: string;
}

/**
 * String literals from API (Separation / Exit V2) — use for display only when
 * the backend does not provide `activeExitStatusMessage`.
 */
export type ExitStatusV2 =
  | "SUBMITTED"
  | "MANAGER_APPROVAL"
  | "MANAGER_APPROVED"
  | "HR_REVIEW"
  | "NOTICE_PERIOD"
  | "EXIT_IN_PROGRESS"
  | "FINAL_SETTLEMENT"
  | "EXIT_COMPLETED"
  | "REJECTED"
  | "WITHDRAWN"
  | "LINKED_TERMINATION_CANCELLED";

/**
 * Termination row status (Termination V2) — mirrors Swagger string enum.
 */
export type TerminationStatusV2 =
  | "TERMINATION_INITIATED"
  | "HR_REVIEW"
  | "NOTICE_PERIOD"
  | "REJECTED"
  | "TERMINATION_CANCELLED"
  | "TERMINATION_COMPLETED";

/**
 * Optional nested object on GET /v2/exit/resignation-form when a termination is
 * in progress. `exitId` is the **exit** (lifecycle) id — not `activeTerminationId`
 * on the parent, which is the **termination request** id.
 */
export interface ResignationFormActiveTerminationSummary {
  /** Mongo id of the exit used for GET /v2/exit/:id/lifecycle (checklist, etc.) */
  exitId?: string | null;
  id?: string | null;
  terminationId?: string | null;
  terminationRequestId?: string | null;
}

/** GET /v2/exit/resignation-form */
export interface ResignationFormContext {
  /** Server-only anchor date (YYYY-MM-DD, UTC) */
  resignationDate: string;
  reasonOptions: ResignationReasonOption[];
  minNoticeEnforced: boolean;
  minNoticeCalendarDays: number;
  canSubmitNewRequest: boolean;
  /**
   * Optional in-flight exit id for the current user (some backends).
   * Lets resignation-only roles open checklist without GET /v2/exit/my-exit.
   */
  activeExitId?: string | null;
  /**
   * Exit id linked to a blocking termination (when `activeTerminationInProgress` is set),
   * or any alternate name the API uses for the same concept.
   */
  linkedExitId?: string | null;
  inFlightExitId?: string | null;
  openExitId?: string | null;
  /**
   * Present for employee resignation path (not for employer-termination-sourced exit).
   */
  activeExitStatus?: ExitStatusV2 | (string & {}) | null;
  /** Shown when canSubmitNewRequest is false (resignation/exit or generic copy) */
  activeExitStatusMessage?: string | null;
  /** true → dedicated “termination in progress” UX (employer termination blocking resignation). */
  activeTerminationInProgress?: boolean;
  activeTerminationId?: string | null;
  activeTerminationStatus?: TerminationStatusV2 | (string & {}) | null;
  /** Human-readable line when a termination is blocking submission */
  activeTerminationMessage?: string | null;
  /**
   * Termination in-flight summary; may include `exitId` for the linked exit
   * (see `ResignationFormActiveTerminationSummary`).
   */
  activeTermination?: ResignationFormActiveTerminationSummary | null;
  /** When false, copy: manager approval starts notice (no separate HR approval step). */
  resignationHrStepEnabled?: boolean;
  /** Escalation hint: “If your manager does not act within X business days…” */
  managerEscalationBusinessDays?: number | null;
}

/** POST /v2/exit/upload-attachment — adjust fields to match your storage DTO */
export interface ExitAttachmentUploadResult {
  attachmentFileUrl?: string;
  attachmentFileKey?: string;
  attachmentOriginalName?: string;
  fileUrl?: string;
  fileKey?: string;
  originalName?: string;
  signedUrl?: string;
  message?: string;
}

/** POST /v2/exit/resign */
export interface SubmitResignationBody {
  reasonId: string;
  /** ISO date string (date-only or full ISO per backend contract) */
  lastWorkingDayRequested: string;
  comments?: string;
  attachmentFileUrl?: string;
  attachmentFileKey?: string;
  attachmentOriginalName?: string;
}

/** PATCH /v2/exit/:id/action — backend enum (lowercase). */
export type ExitWorkflowDecisionApi =
  | "pending"
  | "approved"
  | "rejected"
  | "skipped"
  | "cancelled";

/** UI values for approve/reject (normalized to ExitWorkflowDecisionApi before send). */
export type WorkflowDecision = "APPROVED" | "REJECTED";

/** @deprecated Prefer WorkflowDecision; kept for any legacy callers */
export type ExitDecision = WorkflowDecision;

/** Map form / legacy values to the wire format expected by PATCH .../action. */
export function toExitWorkflowDecisionApi(
  decision: string
): ExitWorkflowDecisionApi {
  return String(decision).toLowerCase() as ExitWorkflowDecisionApi;
}

export interface ExitActionBody {
  decision: ExitWorkflowDecisionApi | WorkflowDecision;
  /** Required when decision is REJECTED (backend validates). */
  remarks?: string;
  /** Manager: optional on approve — applied on first-step approve. */
  suggestedLastWorkingDay?: string;
  /** HR: optional on final-step approve when HR step exists. */
  approvedLastWorkingDay?: string;
}

/** PATCH /v2/exit/:id/manager/suggest-last-day */
export interface SuggestExitLastWorkingDayBody {
  suggestedLastWorkingDay: string;
  remarks?: string;
}

/** Nested employee on GET /v2/exit/my-exit */
export interface MyExitEmployee {
  userId?: string;
  employeeCode?: string;
  fullName?: string;
  workEmail?: string;
  department?: string;
  designation?: string;
  /** Master id when name is not populated */
  employmentType?: string;
  employmentTypeName?: string;
  reportingManagerName?: string;
  functionalManagerName?: string;
  /** Some exit list DTOs embed full user fields here (employment type, etc.). */
  user?: Record<string, unknown>;
}

/** Workflow snapshot on GET /v2/exit/my-exit */
export interface MyExitWorkflow {
  approvalInstanceId?: string;
  workflowStatus?: string;
  workflowStatusLabel?: string;
  isCompleted?: boolean;
  isRejected?: boolean;
  isCancelled?: boolean;
}

export type NoticePeriodDisplayStatus =
  | "NOTICE_PERIOD_ACTIVE"
  | "EXIT_IN_PROGRESS"
  | "OTHER";

export type NoticePeriodDayBasis = "CALENDAR" | "WORKING";

/** Employee notice-period card (when present on my-exit / post-submit). */
export interface NoticePeriodTrackingDto {
  noticePeriodStartDate?: string;
  expectedLastWorkingDay?: string;
  approvedLastWorkingDay?: string | null;
  actualLastWorkingDay?: string;
  remainingCalendarDays?: number;
  displayStatus?: NoticePeriodDisplayStatus;
  noticePeriodDayBasis?: NoticePeriodDayBasis;
}

/** Optional summary from GET /v2/exit/my-exit */
export interface ExitLifecycleSummary {
  checklistTotal?: number;
  checklistCompleted?: number;
  checklistWaived?: number;
  interviewLocked?: boolean;
  settlementStatus?: string;
  settlementNetPayable?: number | null;
  [key: string]: unknown;
}

/**
 * GET /v2/exit/pending-approvals (and similar) — policy / step preview for approvers.
 * @see MyExitResponseDtoV2 / ExitApproverContextDtoV2 (backend)
 */
export interface ExitApproverContextDtoV2 {
  /** Pending request type in approver queue rows. */
  approvalRequestType?: "RESIGNATION_APPROVAL" | "PULLBACK_REQUEST" | string;
  /** 1-based step index for the pending workflow action */
  currentWorkflowStep?: number;
  /** True when the pending actors are on the HR (typically L2) step */
  isHrStep?: boolean;
  /** Policy-based expected last working day (ISO or date) */
  policyExpectedLastWorkingDay?: string;
  /** Notice preview from policy / org */
  noticePeriodCalendarDays?: number;
  noticePeriodDayBasis?: NoticePeriodDayBasis | string;
  /** Org default: resignations usually include an HR approval step */
  resignationHrStepEnabled?: boolean;
  /** This exit instance includes an HR step (false = manager approval completes the flow) */
  thisRequestHasHrStep?: boolean;
  /** Reporting chain exhausted; actors reassigned to HR / next workflow pool */
  lastExitManagerEscalatedToHrPool?: boolean;
  [key: string]: unknown;
}

/** GET /v2/exit/my-exit — extend when backend fields are finalized */
export interface MyExit {
  id: string;
  /** `TERMINATION` = employer-initiated (no employee withdrawal). Omitted on legacy payloads. */
  exitInitiationSource?: "TERMINATION" | "RESIGNATION" | string;
  /** Open linked termination row (e.g. `/separation/termination?openTermination=…`). */
  terminationRequestId?: string;
  /** Legacy top-level status (some responses) */
  status?: ExitStatus | string;
  /** V2 lifecycle code (e.g. MANAGER_APPROVAL) */
  exitStatus?: string;
  reason?: { id?: string; displayName?: string };
  reasonId?: string;
  reasonText?: string;
  resignationDate?: string;
  lastWorkingDayRequested?: string;
  lastWorkingDayApproved?: string;
  managerSuggestedLastWorkingDay?: string;
  noticePeriodDays?: number;
  noticePeriodTracking?: NoticePeriodTrackingDto | null;
  comments?: string;
  remarks?: string;
  minNoticeCalendarDays?: number;
  minNoticeEnforced?: boolean;
  withdrawalReason?: string;
  /** Set when employee has already raised a pullback request awaiting approval. */
  pullbackRequestStatus?: "REQUESTED" | string;
  employee?: MyExitEmployee;
  workflow?: MyExitWorkflow;
  createdAt?: string;
  updatedAt?: string;
  /** Approver queue / rich exit row — step, policy LWD, HR flags */
  approverContext?: ExitApproverContextDtoV2;
  /** When manager escalation fell through to HR pool (may also appear under approverContext) */
  lastExitManagerEscalatedToHrPool?: boolean;
  /** Optional summary from GET /v2/exit/my-exit */
  lifecycleSummary?: ExitLifecycleSummary;
  /** Raw backend fields pass-through */
  [key: string]: unknown;
}

// --- Exit checklist / lifecycle (GET /v2/exit/:id/lifecycle, etc.) ---

export const ExitChecklistDepartment = {
  IT: "IT",
  ADMIN: "ADMIN",
  FINANCE: "FINANCE",
  HR: "HR",
  OTHER: "OTHER",
} as const;

export type ExitChecklistDepartment =
  (typeof ExitChecklistDepartment)[keyof typeof ExitChecklistDepartment];

export const ExitChecklistTaskStatus = {
  Pending: "PENDING",
  InProgress: "IN_PROGRESS",
  Completed: "COMPLETED",
  Waived: "WAIVED",
} as const;

export type ExitChecklistTaskStatus =
  (typeof ExitChecklistTaskStatus)[keyof typeof ExitChecklistTaskStatus];

export interface ExitChecklistTaskDto {
  id: string;
  taskName: string;
  department: ExitChecklistDepartment | string;
  assignedToUserId: string;
  /** When the lifecycle API includes a display name, prefer it over org directory lookup. */
  assignedToName?: string;
  isMandatory: boolean;
  sequence?: number | null;
  status: ExitChecklistTaskStatus | string;
  comments?: string | null;
  completedAt?: string | null;
  completedByUserId?: string | null;
  waivedReason?: string | null;
}

export interface ExitCompletionRequirementsDto {
  requireInterviewSubmitted?: boolean;
  requireSettlementCompleted?: boolean;
}

export interface ExitCompletionGatesDto {
  mandatoryChecklistSatisfied: boolean;
  interviewSatisfied: boolean;
  settlementSatisfied: boolean;
  allSatisfied: boolean;
}

export type ExitInterviewQuestionType =
  | "SHORT_TEXT"
  | "LONG_TEXT"
  | "SINGLE_SELECT"
  | "MULTI_SELECT"
  | "NUMBER"
  | "RATING_1_5"
  | "BOOLEAN"
  | string;

export interface ExitInterviewQuestionDto {
  key?: string;
  questionKey?: string;
  label?: string;
  /** Some APIs return `prompt` where the UI uses `label`. */
  prompt?: string;
  type?: ExitInterviewQuestionType;
  /** Some APIs return `answerType` where the UI uses `type`. */
  answerType?: string;
  /** Display / snapshot order (sort ascending when present). */
  order?: number | null;
  options?: string[];
  required?: boolean;
  [key: string]: unknown;
}

export interface ExitInterviewResponseDto {
  questionKey: string;
  value: string;
}

export interface ExitExitInterviewDto {
  locked?: boolean;
  /** When true, treat like locked for editing (hide save/submit). */
  isSubmitted?: boolean;
  submittedAt?: string | null;
  /** Employee may PATCH responses + submit when true and they have MY_EXIT_MANAGE. */
  employeeSelfSubmitEnabled?: boolean;
  questions?: ExitInterviewQuestionDto[];
  responses?: ExitInterviewResponseDto[];
  [key: string]: unknown;
}

export interface PatchExitInterviewSettingsBody {
  employeeSelfSubmitEnabled?: boolean;
}

export interface SettlementLineDto {
  id?: string;
  label?: string;
  description?: string;
  amount?: number;
  days?: number;
  ratePerDay?: number;
  [key: string]: unknown;
}

export interface ExitSettlementDto {
  status?: string;
  notes?: string | null;
  encashmentLines?: SettlementLineDto[];
  additionLines?: SettlementLineDto[];
  deductionLines?: SettlementLineDto[];
  netPayable?: number | null;
  [key: string]: unknown;
}

export interface ExitLifecycleDetailDto {
  exitId: string;
  exitStatus?: string;
  employeeId?: string;
  checklistTasks?: ExitChecklistTaskDto[];
  appliedChecklistTemplateId?: string | null;
  exitInterview?: ExitExitInterviewDto | null;
  exitSettlement?: ExitSettlementDto | null;
  completionRequirements?: ExitCompletionRequirementsDto | null;
  completionGatesPreview?: ExitCompletionGatesDto | null;
  [key: string]: unknown;
}

export interface ChecklistTemplateItemDto {
  taskName: string;
  department: ExitChecklistDepartment | string;
  isMandatory?: boolean;
  sequence?: number;
  defaultAssigneeUserId?: string;
}

export interface ChecklistTemplateDto {
  id: string;
  name: string;
  department: ExitChecklistDepartment | string;
  items: ChecklistTemplateItemDto[];
  [key: string]: unknown;
}

export interface CreateChecklistTemplateBody {
  name: string;
  department: ExitChecklistDepartment | string;
  items: ChecklistTemplateItemDto[];
}

export interface CreateExitChecklistTaskBody {
  taskName: string;
  department: ExitChecklistDepartment | string;
  assignedToUserId: string;
  isMandatory?: boolean;
  sequence?: number;
}

export interface PatchExitChecklistTaskBody {
  taskName?: string;
  department?: ExitChecklistDepartment | string;
  assignedToUserId?: string;
  isMandatory?: boolean;
  sequence?: number | null;
  status?: ExitChecklistTaskStatus | string;
  comments?: string | null;
}

export interface PutExitSettlementBody {
  encashmentLines?: SettlementLineDto[];
  additionLines?: SettlementLineDto[];
  deductionLines?: SettlementLineDto[];
  notes?: string;
  refreshLeaveBalances?: boolean;
}

export interface ExitInterviewQuestionSetDto {
  id: string;
  name?: string;
  questions?: ExitInterviewQuestionDto[];
  [key: string]: unknown;
}

/** Row in GET /v2/exit/pending-approvals (approver queue) */
export interface ExitPendingApprovalRow extends MyExit {
  userName?: string;
  employeeName?: string;
  employeeId?: string;
  userId?: string;
  department?: string;
  /** When API flattens employment type on the row (else see `employee`) */
  employmentType?: string;
  employmentTypeName?: string;
  statusLabel?: string;
  /** When set, only these users may act (workflow); omitted = client cannot infer — show actions if user has permission. */
  currentActorIds?: string[];
  currentActorId?: string;
}

export interface ExitPendingListPayload {
  data: ExitPendingApprovalRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * NestJS HTTP exception body on `error.response.data`.
 * `message` may be a string or validation array.
 */
export interface NestExceptionBody {
  statusCode?: number;
  message?: string | string[];
  error?: string;
}

export type ExitApiErrorData = NestExceptionBody;

export function isNestExceptionBody(data: unknown): data is NestExceptionBody {
  if (data === null || typeof data !== "object") return false;
  return "message" in data || "statusCode" in data || "error" in data;
}

/** Prefer Nest `response.data.message`; falls back to Error.message. */
export function getExitNestErrorMessage(err: unknown): string {
  if (err && typeof err === "object") {
    const anyErr = err as { data?: unknown; message?: string };
    if (anyErr.data !== undefined && isNestExceptionBody(anyErr.data)) {
      const { message } = anyErr.data;
      if (message !== undefined) {
        return Array.isArray(message) ? message.join(" ") : String(message);
      }
      if (anyErr.data.error) return String(anyErr.data.error);
    }
    if (typeof anyErr.message === "string" && anyErr.message.length > 0) {
      return anyErr.message;
    }
  }
  if (typeof err === "string") return err;
  return "Something went wrong.";
}

// --- Termination (GET /v2/termination/form-context, list, detail, initiate) ---

/**
 * GET /v2/termination/separation-gate/employee/:employeeId
 * — pre-check before initiating a termination for the subject employee.
 */
export interface TerminationSubjectSeparationGateDtoV2 {
  canInitiateTermination: boolean;
  hasBlockingExitRequest?: boolean;
  hasActiveTerminationRequest?: boolean;
  activeExitStatus?: ExitStatusV2 | (string & {}) | null;
  activeTerminationId?: string | null;
  activeTerminationStatus?: TerminationStatusV2 | (string & {}) | null;
  /** Shown when canInitiateTermination is false */
  blockMessage?: string | null;
}

export interface TerminationTypeOption {
  id: string;
  displayName: string;
  filterCode?: string;
}

/** GET /v2/termination/form-context */
export interface TerminationFormContext {
  terminationTypeOptions: TerminationTypeOption[];
  /** YYYY-MM-DD UTC calendar day */
  serverDate: string;
  /**
   * When false, HR/super-admin–initiated terminations skip the second-approver
   * queue and are treated as approved for workflow purposes after submit.
   */
  hrInitiatedTerminationSecondApproverEnabled?: boolean;
  /**
   * Employee email timing: `immediate` vs `after_approval` (or similar enum
   * strings from the API — compared case-insensitively in the UI).
   */
  notifyEmployeeOnTerminationInitiated?: string;
}

/** POST /v2/termination/upload-supporting-document */
export interface TerminationDocumentUploadResult {
  message?: string;
  fileUrl?: string;
  /** API may return storage key as `key` */
  key?: string;
  fileKey?: string;
  originalName?: string;
}

export interface TerminationSupportingDocumentRef {
  fileUrl?: string;
  fileKey?: string;
  originalName?: string;
}

/** POST /v2/termination/initiate */
export interface InitiateTerminationBody {
  employeeId: string;
  terminationTypeId: string;
  reasonJustification: string;
  /** ISO date string */
  lastWorkingDay: string;
  supportingDocuments?: TerminationSupportingDocumentRef[];
  /** Uploaded warning / PIP files — matches backend field `warningPipDocuments`. */
  warningPipDocuments?: TerminationSupportingDocumentRef[];
  confidentialityNote?: string;
}

export type TerminationDecision = "approved" | "rejected";

export interface TerminationActionBody {
  decision: TerminationDecision;
  remarks?: string;
}

export interface TerminationCancelBody {
  reason?: string;
}

export interface TerminationEmployeeSnapshot {
  userId?: string;
  employeeCode?: string;
  fullName?: string;
  workEmail?: string;
  department?: string;
  designation?: string;
  reportingManagerName?: string;
  functionalManagerName?: string;
  /** When API sends only a master id; `employmentTypeDisplay` resolves the label. */
  employmentType?: string;
  employmentTypeName?: string;
  /** Nested user payload may carry employment type fields. */
  user?: Record<string, unknown>;
}

export interface TerminationTypeSnapshot {
  id?: string;
  displayName?: string;
}

export interface TerminationWorkflowSnapshot {
  approvalInstanceId?: string;
  workflowStatus?: string;
  workflowStatusLabel?: string;
  isCompleted?: boolean;
  isRejected?: boolean;
  isCancelled?: boolean;
}

/** GET /v2/termination, GET /v2/termination/:id */
export interface TerminationRequestDto {
  id: string;
  terminationStatus?: string;
  employeeId?: string;
  initiatedByUserId?: string;
  terminationType?: TerminationTypeSnapshot;
  reasonJustification?: string;
  lastWorkingDay?: string;
  supportingDocuments?: TerminationSupportingDocumentRef[];
  warningPipDocuments?: TerminationSupportingDocumentRef[];
  confidentialityNote?: string;
  noticePeriodDays?: number;
  initiationDate?: string;
  employee?: TerminationEmployeeSnapshot;
  workflow?: TerminationWorkflowSnapshot;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}
